/**
 * Machine ID Guard Component
 * Ensures the app has a valid machineId before allowing the app to load.
 * This prevents authentication and matchmaking failures due to missing/invalid machineId.
 *
 * NOTE: Only runs in Electron. Remote dashboard bypasses this check.
 */

import React, { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { getElectronStores } from '../api/stores/electronStores';

const TIMEOUT_MS = 10000; // 10 seconds max wait
const RESYNC_REQUEST_MS = 3000; // Request resync at 3s if still waiting

type MachineIdState =
  | { status: 'loading' }
  | { status: 'valid'; machineId: string }
  | { status: 'error'; error: string; details?: string };

export const MachineIdGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Check if we're running in Electron - only validate machineId for Electron
  const isElectron = typeof window !== 'undefined' && window.playcast !== undefined;

  const [state, setState] = useState<MachineIdState>({ status: 'loading' });

  useEffect(() => {
    // If not Electron (e.g., remote dashboard), skip validation entirely
    if (!isElectron) {
      console.log('[MACHINE_ID_GUARD] Not in Electron, skipping validation');
      setState({ status: 'valid', machineId: 'remote-dashboard' });
      return;
    }
    console.log('[MACHINE_ID_GUARD] Initializing guard...');

    const electronStores = getElectronStores();
    // eslint-disable-next-line prefer-const
    let timeoutId: NodeJS.Timeout;
    let resyncTimerId: NodeJS.Timeout | null = null;
    let listenerCleanup: (() => void) | null = null;
    let sentryReported = false;
    let resolved = false;

    const validateMachineId = (id: string | undefined): MachineIdState => {
      console.log('[MACHINE_ID_GUARD] Validating machineId:', id);

      if (!id || id.trim() === '') {
        const error = 'Machine ID is empty or undefined';
        console.error('[MACHINE_ID_GUARD]', error);

        if (!sentryReported) {
          sentryReported = true;
          Sentry.captureException(new Error(error), {
            level: 'fatal',
            tags: { issue_type: 'empty_machine_id_guard' },
            extra: { machineId: id || '(undefined)' },
          });
        }

        return {
          status: 'error',
          error: 'Setup Incomplete',
          details: 'Your Playcast installation is missing important setup information. Please reinstall the application to fix this.',
        };
      }

      console.log('[MACHINE_ID_GUARD] Valid machineId:', id);
      return { status: 'valid', machineId: id };
    };

    const onResolved = () => {
      resolved = true;
      if (timeoutId) clearTimeout(timeoutId);
      if (resyncTimerId) clearTimeout(resyncTimerId);
    };

    const checkMachineId = () => {
      const currentId = electronStores.machineIdState.currentState;
      console.log('[MACHINE_ID_GUARD] Checking current state:', currentId);

      if (currentId && currentId.trim() !== '') {
        console.log('[MACHINE_ID_GUARD] MachineId available immediately, validating...');
        const result = validateMachineId(currentId);
        setState(result);
        onResolved();
      } else {
        console.log('[MACHINE_ID_GUARD] MachineId not ready, setting up listener...');

        resyncTimerId = setTimeout(() => {
          if (resolved) return;
          console.warn('[MACHINE_ID_GUARD] Still waiting at 3s, requesting IPC resync');
          window.playcast?.requestResync?.();
        }, RESYNC_REQUEST_MS);

        const listener = electronStores.machineIdState.addListener((id) => {
          console.log('[MACHINE_ID_GUARD] Received machineId update from listener:', id);

          if (id && id.trim() !== '') {
            const result = validateMachineId(id);
            setState(result);
            onResolved();
          } else {
            console.log('[MACHINE_ID_GUARD] Received empty value, continuing to wait...');
          }
        });

        listenerCleanup = () => {
          electronStores.machineIdState.removeListener(listener);
        };
      }
    };

    // Set timeout for machineId arrival
    timeoutId = setTimeout(() => {
      const currentMachineIdValue = electronStores.machineIdState.currentState;
      console.error('[MACHINE_ID_GUARD] Timeout waiting for machineId');
      console.error('[MACHINE_ID_GUARD] Current machineId value at timeout:', currentMachineIdValue || '(undefined)');
      console.error('[MACHINE_ID_GUARD] This indicates the machineId was not provided within 10 seconds');

      // Only report to Sentry once
      if (!sentryReported) {
        sentryReported = true;
        // Capture critical startup timeout to Sentry
        Sentry.captureException(new Error('Machine ID startup timeout - App failed to initialize within 10 seconds'), {
          level: 'fatal',
          tags: {
            issue_type: 'machine_id_timeout',
            machine_id_empty: !currentMachineIdValue || currentMachineIdValue.trim() === '' ? 'true' : 'false',
            machine_id_is_error: currentMachineIdValue?.startsWith('ERROR_') ? 'true' : 'false',
          },
          extra: {
            timeoutMs: TIMEOUT_MS,
            machineIdValue: currentMachineIdValue || '(undefined)',
            machineIdLength: currentMachineIdValue?.length || 0,
            timestamp: new Date().toISOString(),
          },
          contexts: {
            machine_state: {
              machineId: currentMachineIdValue || '(undefined)',
              isEmpty: !currentMachineIdValue || currentMachineIdValue.trim() === '',
              isError: currentMachineIdValue?.startsWith('ERROR_') || false,
            },
          },
        });
      }

      setState({
        status: 'error',
        error: 'Startup Timeout',
        details: `The application is taking longer than expected to start. Please restart Playcast and try again.`,
      });
    }, TIMEOUT_MS);

    checkMachineId();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (resyncTimerId) clearTimeout(resyncTimerId);
      if (listenerCleanup) listenerCleanup();
    };
  }, [isElectron]);

  if (state.status === 'loading') {
    return (
      <div className="flex h-screen items-center justify-center flex-col gap-5 bg-gray-900 text-gray-100">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary/20 border-t-primary" />
        {/* <div className="text-lg font-medium text-gray-400">
          Starting Playcast...
        </div> */}
      </div>
    );
  }

  if (state.status === 'error') {
    const handleClose = () => {
      console.log('[MACHINE_ID_GUARD] Close button clicked - closing application');
      // Close the Electron window
      // Electron windows support window.close()
      if (typeof window !== 'undefined') {
        window.close();
      }
    };

    return (
      <div className="flex h-screen items-center justify-center bg-gray-900 text-gray-100 p-10">
        <div className="max-w-xl text-center">
          {/* Warning Icon */}
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-red-500/20 to-red-900/20 text-4xl">
            <span role="img" aria-label="warning">⚠️</span>
          </div>

          {/* Error Title */}
          <h1 className="mb-4 text-3xl font-bold tracking-tight text-gray-100">
            {state.error}
          </h1>

          {/* Error Description */}
          <p className="mb-8 text-base leading-relaxed text-gray-400">
            {state.details}
          </p>

          {/* Action Steps */}
          <div className="mb-8 rounded-xl border border-red-500/40 bg-gray-800 p-6 text-left text-sm leading-relaxed">
            <div className="mb-4 text-[15px] font-semibold text-gray-100">
              What to do next:
            </div>
            <ol className="m-0 space-y-2 pl-6 text-gray-400">
              <li>Close Playcast completely</li>
              <li>Download and run the installer again</li>
              <li>If this keeps happening, contact our support team</li>
            </ol>
          </div>

          {/* Support Link */}
          <div className="mb-6 rounded-lg border border-primary/30 bg-primary/15 p-4">
            <div className="mb-2 text-sm text-gray-400">
              Need help? Visit our support page:
            </div>
            <div className="text-sm font-medium text-primary cursor-pointer" onClick={() => window.open('https://discord.gg/playcast', '_blank')}>
              discord.gg/playcast
            </div>
          </div>

          {/* Close Button */}
          <button
            onClick={handleClose}
            className="w-full rounded-lg bg-red-600 px-6 py-3 font-semibold text-white transition-colors hover:bg-red-700 active:bg-red-800"
          >
            Close Playcast
          </button>
        </div>
      </div>
    );
  }

  // Valid state - render children
  return children;
};

